import React, { type ReactElement, useCallback, useState } from 'react';
import { Button as FlowbiteButton, Dropdown } from 'flowbite-react';
import { type ComponentSize, type SelectMenuItem } from './__commons__/selectCommons';
import { dropdownTheme } from '../../styles/themes/select';
import { Text } from '../text';
import { buttonIconTheme, selectButtonTheme } from '../../styles/themes/button';
import { IconLoader } from '../iconLoader/iconLoader';

export interface SelectProps {
    value?: string;
    middleMenuItems: SelectMenuItem[];
    bottomMenuItems?: SelectMenuItem[];
    label?: string;
    helper?: string;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'left-start' | 'right-start';
    testId?: string;
    displaySelectedIcon?: boolean;
    variant?: string;
    size?: ComponentSize;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    disabled?: boolean;
    header?: header;
    onItemSelect?: (item: SelectMenuItem) => void;
    id: string;
    pill?: boolean;
    arrow?: boolean;
    renderTrigger?: () => ReactElement;
    clearable?: boolean;
    buttonClassName?: string;
}

export interface header {
    title: string;
    subtitle: string;
}

export const Select: React.FC<SelectProps> = (props) => {
    const {
        value,
        middleMenuItems,
        bottomMenuItems,
        label,
        helper,
        displaySelectedIcon = true,
        placement = 'bottom',
        testId,
        disabled,
        variant = 'primary',
        size = 'sm',
        header,
        onItemSelect,
        id,
        startIcon,
        endIcon,
        pill,
        arrow = true,
        renderTrigger,
        clearable = true,
        buttonClassName,
    } = props;

    const initialSelectedItem = middleMenuItems.find((item) => item.isSelected);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(
        initialSelectedItem ? initialSelectedItem.id : null,
    );

    const clearSelection = useCallback(() => {
        setSelectedItemId(null);
    }, []);

    const renderArrow = useCallback(
        (buttonElement: HTMLButtonElement | null) => {
            const arrowClass = 'stroke-system-on-surface-variant fill-system-on-surface-variant';

            if (placement.includes('bottom')) {
                return (
                    <>
                        {buttonElement?.ariaExpanded === 'true' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronUp'} />
                        )}
                        {buttonElement?.ariaExpanded === 'false' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronDown'} />
                        )}
                    </>
                );
            }
            if (placement.includes('top')) {
                return (
                    <>
                        {buttonElement?.ariaExpanded === 'true' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronDown'} />
                        )}
                        {buttonElement?.ariaExpanded === 'false' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronUp'} />
                        )}
                    </>
                );
            }
            if (placement.includes('left')) {
                return (
                    <>
                        {buttonElement?.ariaExpanded === 'true' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronRight'} />
                        )}
                        {buttonElement?.ariaExpanded === 'false' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronLeft'} />
                        )}
                    </>
                );
            }
            if (placement.includes('right')) {
                return (
                    <>
                        {buttonElement?.ariaExpanded === 'true' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronLeft'} />
                        )}
                        {buttonElement?.ariaExpanded === 'false' && (
                            <IconLoader className={arrowClass} size='sm' iconName={'IconChevronRight'} />
                        )}
                    </>
                );
            }
        },
        [placement],
    );

    const dropdownRenderButtonTrigger = useCallback(() => {
        // Arrow issue workaround
        const triggerId = `${id}-dropdownTrigger`;
        const buttonElement = document.getElementById(triggerId) as HTMLButtonElement | null;

        return (
            <FlowbiteButton
                pill={pill}
                id={triggerId}
                size={size}
                color={variant}
                theme={pill ? buttonIconTheme : selectButtonTheme}
                className={buttonClassName}
            >
                {startIcon && <div className='flex flex-shrink-0 justify-flex-start'>{startIcon}</div>}

                <div className='flex-1 text-left text-system-on-surface truncate'>
                    {value
                        ? selectedItemId
                            ? [...middleMenuItems, ...(bottomMenuItems ?? [])].find(
                                  (item) => item.id === selectedItemId,
                              )?.value
                            : value
                        : value}
                </div>

                <div className='flex items-center gap-1 flex-shrink-0 justify-end'>
                    {clearable && selectedItemId && (
                        <div onClick={clearSelection} className='flex cursor-pointer'>
                            <IconLoader
                                size='sm'
                                iconName={'IconRemove'}
                                className='stroke-system-on-surface-variant'
                            />
                        </div>
                    )}
                    {endIcon && <div className='flex'>{endIcon}</div>}
                    {arrow && <div className='flex'>{renderArrow(buttonElement)}</div>}
                </div>
            </FlowbiteButton>
        );
    }, [
        value,
        variant,
        size,
        id,
        pill,
        startIcon,
        endIcon,
        arrow,
        renderArrow,
        selectedItemId,
        middleMenuItems,
        bottomMenuItems,
        clearable,
        clearSelection,
        buttonClassName,
    ]);

    const renderMiddleSelectItem = useCallback(
        (item: SelectMenuItem, textColor: string) => {
            const handleItemClick = () => {
                setSelectedItemId(item.id);
                onItemSelect?.(item);
                item.onClick?.();
            };

            return (
                <Dropdown.Item key={item.id} className={textColor} icon={item?.icon} onClick={handleItemClick}>
                    <div className='flex items-center w-full justify-between'>
                        <Text variant='label-large'>{item.value}</Text>
                        {displaySelectedIcon && selectedItemId === item.id && (
                            <IconLoader
                                iconName={'IconCheck'}
                                size='sm'
                                data-testid={item.id + '-checkmark'}
                                className='justify-end'
                            />
                        )}
                    </div>
                </Dropdown.Item>
            );
        },
        [onItemSelect, displaySelectedIcon, selectedItemId],
    );

    const renderBottomItem = useCallback((item: SelectMenuItem, textColor: string) => {
        const handleItemClick = () => {
            // Only trigger the action but do not update selected state
            item.onClick?.();
        };

        return (
            <Dropdown.Item key={item.id} className={textColor} icon={item?.icon} onClick={handleItemClick}>
                <div className='flex items-center w-full justify-between'>
                    <Text variant='title-small'>{item.value}</Text>
                </div>
            </Dropdown.Item>
        );
    }, []);

    return (
        <div className='flex flex-col gap-1'>
            {label && (
                <div className='pb-2'>
                    <Text variant='label-large'>{label}</Text>
                </div>
            )}
            <Dropdown
                disabled={disabled}
                placement={placement}
                data-testid={testId}
                label={value}
                renderTrigger={renderTrigger ?? dropdownRenderButtonTrigger}
                theme={dropdownTheme}
            >
                {header && (
                    <Dropdown.Header>
                        <Text variant={header.subtitle ? 'label-large' : 'title-medium'}>{header.title}</Text>
                        <Text variant='label-large' className='text-system-on-surface-variant'>
                            {header.subtitle}
                        </Text>
                    </Dropdown.Header>
                )}
                {middleMenuItems.map((item) => renderMiddleSelectItem(item, ''))}
                {bottomMenuItems && <Dropdown.Divider />}
                {bottomMenuItems?.map((item) => renderBottomItem(item, 'text-system-on-surface'))}
            </Dropdown>
            {helper && (
                <div className='pt-2'>
                    <Text variant='label-large' className='text-system-on-surface-variant'>
                        {helper}
                    </Text>
                </div>
            )}
        </div>
    );
};
